import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { PrismCode } from 'react-prism';
import remarkGfm from 'remark-gfm';
import type { Message } from '@dev-streetbeat/chat';
import styles from './../../chat.module.scss';

import React from 'react';
import rehypeKatex from 'rehype-katex';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import colorsStore from 'shared/store/colorsStore';
import { preprocessLaTeX } from 'utils/textUtils';
import { TableWrapper } from './TableWrapper';
import rehypeRaw from 'rehype-raw';
import { ChatAgentCheckIcon } from 'shared/assets/svg/ChatAgentCheckIcon';
import { useFileOperations, useFiles } from '@dev-streetbeat/chat';

type Props = {
  message: Message | string;
  onPortfolio?: boolean;
  compact?: boolean;
};

export const TextResponse: React.FC<Props> = ({ message, onPortfolio = false, compact = false }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const textContent = typeof message === 'string' ? message : message?.content?.[0]?.text?.value;

  const { files } = useFiles();
  const { downloadFile } = useFileOperations();

  const latexSafeContent = preprocessLaTeX(textContent || '');

  const customRenderers = {
    table: ({ children }) => <TableWrapper>{children}</TableWrapper>,
    a: ({ children, ...props }) => {
      const handleClick = async () => {
        const file = files?.find((file) => file.name === props.href);
        const fileName = file.name;
        const fileId = file.id;

        const response = await downloadFile(fileId);

        const link = document.createElement('a');
        link.href = URL.createObjectURL(response);
        link.download = fileName;
        link.click();
      };

      return (
        <button className={styles.chatACopy} {...props} onClick={handleClick}>
          {children}
        </button>
      );
    },
    pre(props: any) {
      const className = props.node.children?.[0]?.properties?.className?.[0];
      return (
        <PrismCode
          component="pre"
          className={`font-proxima rounded-xl font-sans text-[12px] !text-FinecoBlack shadow-none ${className}`}
          style={{ textShadow: 'none' }}
          {...props.attributes}
        >
          {props.children}
        </PrismCode>
      );
    },
    checkicon: () => (
      <span style={{ display: 'inline-flex', marginRight: 6 }}>
        <ChatAgentCheckIcon />
        <div>ciao ciao</div>
      </span>
    ),
    img(props: any) {
      return (
        <div className="my-8 flex justify-center">
          <img {...props} />
        </div>
      );
    },
    p: ({ children, ...props }) => {
      return (
        <p className={classNames(styles.chatPCopy, compact && styles.chatPCopyCompact)} {...props}>
          {children}
        </p>
      );
    },
    li: ({ children, ...props }) => {
      return (
        <li style={{ color: colorsConfig.chatCopyColor }} {...props}>
          {children}
        </li>
      );
    },
    h1: ({ node, ...props }) => <h1 style={{ color: colorsConfig.chatCopyColor }} {...props} />,
    h2: ({ node, ...props }) => <h2 style={{ color: colorsConfig.chatCopyColor }} {...props} />,
    h3: ({ node, ...props }) => <h3 style={{ color: colorsConfig.chatCopyColor }} {...props} />,
    strong: ({ node, ...props }) => <strong style={{ color: colorsConfig.chatCopyColor }} {...props} />,
  };

  return (
    <div className={!onPortfolio ? styles.reactMarkdownContainer : styles.reactMarkdownContainerOnPortfolio}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
        rehypePlugins={[rehypeKatex, rehypeRaw]}
        components={customRenderers}
        className={classNames(styles.reactMarkdown, styles.chatMainCopy)}
      >
        {latexSafeContent}
      </ReactMarkdown>
    </div>
  );
};
